import { faSyncAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { KeycloakProfile } from "keycloak-js";
import React, { useCallback, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { FormattedMessage, IntlShape } from "react-intl";
import styled from "styled-components";
import SweetAlert from "sweetalert2";
import { useKeycloak } from "../../components/keycloak-provider";
import { useAuthorizer } from "../../lib/action-authorizer";
import authorizeActions from "../../lib/actions";
import { useBackofficeClient } from "../../lib/http";
import * as loyaltyApi from "../shared/api/loyaltyApi";
import { showErrorAlert } from "../shared/crud/error-messages";
import { defaultCrudMessages } from "../shared/crud/simple-crud";
import { promptFileDownloadFromBlob } from "../shared/file";
import { APP_COLORS } from "../witadmin/Common/constants";
import { AxiosError } from "axios";

const messages = {
  ...defaultCrudMessages,
  exportWithValuesOnly: {
    id: "loyaltyCustomerTransactionReport.exportWithValuesOnly",
    defaultMessage: "Export all",
  },
  exportUnmasked: {
    id: "loyaltyCustomerTransactionReport.exportUnmasked",
    defaultMessage: "Export unmasked",
  },
  sendUnmaskedReportSuccess: {
    id: "loyaltyCustomerTransactionReport.sendUnmaskedReportSuccess",
    defaultMessage:
      "You will receive an email at {email} with the customer transaction report attached",
  },
  sendUnmaskedReportError: {
    id: "loyaltyCustomerTransactionReport.sendUnmaskedReportError",
    defaultMessage:
      "An error occurred trying to send unmasked customer transaction report by email",
  },
  unmaskedErrorTooManyRequests: {
    id: "loyaltyCustomer.unmaskedErrorTooManyRequests",
    defaultMessage:
      "You've reached the maximum unmasked requests by minute. Please, try again later.",
  },
  ok: {
    id: "default.ok",
    defaultMessage: "OK",
  },
  maximumExceed: {
    id: "loyaltyCustomerTransactionReport.error.maximumExceed",
    defaultMessage:
      "El número de usuarios supera los 50,000. Por favor, contacte con el soporte técnico para obtener asistencia.",
  },
  errorTitle: {
    id: "loyaltyCustomerTransactionReport.error.title",
    defaultMessage: "Error",
  },
};

const TableActionButton = styled(Button)`
  height: 33px;
  margin-right: 14px;
`;

export interface ExportActionProps {
  brandTicker: string;
  brandId: string;
  intl: IntlShape;
  filters: Record<string, string>;
  disabledExport: boolean;
}

export function ExportAction(props: ExportActionProps): JSX.Element {
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isUnmaskedLoading, setUnmaskedLoading] = useState<boolean>(false);
  const [userProfile, setUserProfile] = useState<KeycloakProfile | undefined>();

  const authorizer = useAuthorizer();
  const keycloak = useKeycloak();
  const backofficeClient = useBackofficeClient();

  const canViewUnmaskedData = authorizer.canExecute(authorizeActions.ExportMaskedData);

  useEffect(() => {
    async function loadProfile() {
      const profile = await keycloak.loadUserProfile();
      setUserProfile(profile);
    }

    loadProfile().catch(err => console.error("Error loading user profile", err));
  }, [keycloak]);

  const handleExport = useCallback(async () => {
    if (props.disabledExport) {
      await showErrorAlert({
        title: props.intl.formatMessage(messages.errorTitle),
        text: props.intl.formatMessage(messages.maximumExceed),
        intl: props.intl,
      });
      return;
    }
    setLoading(true);
    try {
      const file = await loyaltyApi.customerTransactionReportFullExport(backofficeClient, {
        brandTicker: props.brandTicker,
        brandId: props.brandId,
        filters: props.filters,
      });
      promptFileDownloadFromBlob(file, "full-report.xlsx");
    } catch (e) {
      await SweetAlert.fire({
        title: props.intl.formatMessage(messages.loadError),
        text: props.intl.formatMessage(messages.error),
        icon: "error",
        confirmButtonText: props.intl.formatMessage(messages.ok),
        confirmButtonColor: APP_COLORS.green,
      });
    }
    setLoading(false);
  }, [
    backofficeClient,
    props.brandId,
    props.brandTicker,
    props.disabledExport,
    props.filters,
    props.intl,
  ]);

  const handleUnmaskedExport = useCallback(async () => {
    try {
      setUnmaskedLoading(true);
      // Through backoffice-manager: customer-rewards only sends the unmasked report when the proxy
      // has checked the property against the brand.
      await backofficeClient.post(
        `/v1/loyalty/proxy/${props.brandTicker}/report/customer-transaction-report/send`,
        { brandIdentifier: props.brandId, filters: props.filters }
      );

      await SweetAlert.fire({
        icon: "info",
        text: props.intl.formatMessage(messages.sendUnmaskedReportSuccess, {
          email: userProfile?.email,
        }),
        cancelButtonText: `${props.intl.formatMessage(messages.ok)}`,
      });
    } catch (e) {
      if (e instanceof AxiosError && e.response && e.response.status === 429) {
        await SweetAlert.fire({
          icon: "warning",
          text: props.intl.formatMessage(messages.unmaskedErrorTooManyRequests),
          cancelButtonText: `${props.intl.formatMessage(messages.ok)}`,
        });
      } else {
        await SweetAlert.fire({
          icon: "error",
          text: props.intl.formatMessage(messages.sendUnmaskedReportError),
          cancelButtonText: `${props.intl.formatMessage(messages.ok)}`,
        });
      }
    } finally {
      setUnmaskedLoading(false);
    }
  }, [
    backofficeClient,
    props.brandId,
    props.brandTicker,
    props.filters,
    props.intl,
    userProfile?.email,
  ]);

  return (
    <>
      {canViewUnmaskedData && (
        <TableActionButton onClick={handleUnmaskedExport} disabled={isUnmaskedLoading}>
          {isUnmaskedLoading && <FontAwesomeIcon icon={faSyncAlt} spin />}{" "}
          <FormattedMessage {...messages.exportUnmasked} />
        </TableActionButton>
      )}
      <TableActionButton onClick={handleExport} disabled={isLoading}>
        {isLoading && <FontAwesomeIcon icon={faSyncAlt} spin />}{" "}
        <FormattedMessage {...messages.exportWithValuesOnly} />
      </TableActionButton>
    </>
  );
}
